/**
 * StoryVetrinaAgente — Instagram Story 9:16 (1080×1920) della vetrina
 * personale dell'agente.
 *
 * iter v32 r48 — User M1452: «mandami la preview di una Story IG bella
 * che condivide la vetrina».
 *
 * Layout cinematic editoriale:
 *  · Top 0-1180: foto immobile featured full-bleed con gradient verso il cream
 *  · Sopra il gradient: badge "VETRINA · PRONTO" + claim eyebrow piccolo
 *  · Bottom 1180-1920: cream con foto agente cerchio sage + nome + tagline
 *    + linea accent + bio + CTA URL
 *
 * URL: /share/story-vetrina?slug=apple-test
 * (clean=1 disabilita wrapper dark per export)
 */
import React from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const PALETTE = {
  cream: "#F5F0E6",
  ink: "#1A1715",
  inkSoft: "#5A524A",
  sage: "#7C8B6E",
  caramel: "#B57C5E",
  paper: "#F8F4EC",
  divider: "rgba(26,23,21,0.14)",
};

function useVetrinaData(slug) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  React.useEffect(() => {
    if (!slug) {
      setState({ loading: false, data: null, error: "no-slug" });
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/agente/${slug}`);
        if (!r.ok) throw new Error(`http-${r.status}`);
        const d = await r.json();
        if (!cancel) setState({ loading: false, data: d, error: null });
      } catch (e) {
        if (!cancel) setState({ loading: false, data: null, error: String(e) });
      }
    })();
    return () => { cancel = true; };
  }, [slug]);
  return state;
}

function agencyTagline(data) {
  const ag = (data?.agency_name || "").trim();
  const nm = (data?.agent_display_name || "").trim();
  if (!ag) return "Agente immobiliare";
  if (ag.toLowerCase() === nm.toLowerCase()) return ag;
  return `Agente immobiliare per ${ag}`;
}

function pickFeaturedPhoto(data) {
  const props = data?.properties || [];
  for (const p of props) {
    const ph = p?.cover_photo_url || p?.cover_url || p?.photo_url || (p?.photos && p.photos[0]);
    if (ph) return absUrl(ph);
  }
  return null;
}

function pickAgentPhoto(data) {
  const ph = data?.agent_photo_url || data?.photo_url || null;
  return ph ? absUrl(ph) : null;
}

function absUrl(u) {
  if (!u) return u;
  if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("data:")) return u;
  if (u.startsWith("/")) return `${API}${u}`;
  return u;
}

export default function StoryVetrinaAgente() {
  const slug = React.useMemo(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("slug") || "apple-test";
  }, []);
  const cleanMode = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("clean") === "1";
  }, []);

  const { loading, data, error } = useVetrinaData(slug);
  const [scale, setScale] = React.useState(0.36);

  React.useEffect(() => {
    if (cleanMode) { setScale(1); return; }
    const fit = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const s = Math.min((w - 32) / 1080, (h - 80) / 1920);
      setScale(Math.max(0.18, Math.min(1, s)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [cleanMode]);

  if (loading) {
    return <div data-testid="story-vetrina-page" style={{ minHeight: "100vh", background: "#0F0E0C", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',sans-serif" }}>Caricamento…</div>;
  }
  if (error || !data) {
    return <div data-testid="story-vetrina-page" style={{ minHeight: "100vh", background: "#0F0E0C", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',sans-serif" }}>Vetrina non disponibile</div>;
  }

  const agentName = (data.agent_display_name || "").trim();
  const tagline = agencyTagline(data);
  const bio = (data.bio || "").trim();
  const featuredPhoto = pickFeaturedPhoto(data);
  const agentPhoto = pickAgentPhoto(data);
  const url = `prontoimmo.it/agente/${slug}`;
  const propertiesCount = data.properties_count || (data.properties || []).length;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: cleanMode ? "#FFFFFF" : "#0F0E0C",
        padding: cleanMode ? 0 : "40px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Montserrat','Inter',sans-serif",
      }}
      data-testid="story-vetrina-page"
    >
      <div style={{ width: 1080 * scale, height: 1920 * scale }}>
        <div
          data-testid="story-canvas"
          style={{
            width: 1080,
            height: 1920,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            position: "relative",
            background: PALETTE.cream,
            overflow: "hidden",
            boxShadow: cleanMode ? "none" : "0 60px 120px rgba(0,0,0,0.5)",
            color: PALETTE.ink,
          }}
        >
          {/* ═════════════════════════════════════════════════
              TOP · FOTO IMMOBILE FULL-BLEED (0 → 1180)
              ═════════════════════════════════════════════════ */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 1180,
              overflow: "hidden",
              background: "#1A1715",
            }}
          >
            {featuredPhoto ? (
              <img
                src={featuredPhoto}
                alt=""
                crossOrigin="anonymous"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  filter: "saturate(0.92) brightness(0.96)",
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: `linear-gradient(135deg, ${PALETTE.sage} 0%, #4A5644 100%)`,
                }}
              />
            )}

            {/* Scrim scuro al centro per far risaltare il claim */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.28) 65%, rgba(0,0,0,0) 80%)",
              }}
            />

            {/* Vignette ai bordi per dare profondità */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(ellipse at center, rgba(0,0,0,0) 45%, rgba(0,0,0,0.32) 100%)",
              }}
            />

            {/* Gradient inferiore verso il cream */}
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 320,
                background: `linear-gradient(180deg, rgba(245,240,230,0) 0%, ${PALETTE.cream} 90%)`,
              }}
            />
          </div>

          {/* ═════════════════════════════════════════════════
              TOP BADGE · "VETRINA · PRONTO" (centrato in alto)
              ═════════════════════════════════════════════════ */}
          <div
            style={{
              position: "absolute",
              top: 80,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 14,
            }}
          >
            <div
              style={{
                padding: "12px 22px",
                background: "rgba(255,255,255,0.96)",
                borderRadius: 999,
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: "0.34em",
                color: PALETTE.ink,
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                gap: 10,
                boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 999,
                  background: PALETTE.sage,
                  boxShadow: `0 0 0 4px rgba(124,139,110,0.28)`,
                }}
              />
              Vetrina · Pronto
            </div>
          </div>

          {/* ═════════════════════════════════════════════════
              HERO CLAIM SOPRA FOTO · centrato verticale sull'immagine
              ═════════════════════════════════════════════════ */}
          <div
            style={{
              position: "absolute",
              top: 420,
              left: 70,
              right: 70,
              textAlign: "center",
              filter: "drop-shadow(0 4px 18px rgba(0,0,0,0.45))",
            }}
          >
            <div
              style={{
                display: "inline-block",
                padding: "8px 18px",
                background: "rgba(255,255,255,0.14)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                border: "1px solid rgba(255,255,255,0.18)",
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.34em",
                color: "rgba(255,255,255,0.98)",
                textTransform: "uppercase",
                marginBottom: 30,
              }}
            >
              · Sito personale online ·
            </div>
            <div
              style={{
                fontFamily: "'Tinos','Cormorant Garamond',serif",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 76,
                lineHeight: 1.02,
                letterSpacing: "-0.018em",
                color: "#FFFFFF",
                textShadow:
                  "0 4px 28px rgba(0,0,0,0.65), 0 2px 8px rgba(0,0,0,0.4)",
              }}
            >
              I miei immobili,<br />a portata di link.
            </div>
          </div>

          {/* ═════════════════════════════════════════════════
              BOTTOM · CARD AGENTE (1180 → 1920) cream
              ═════════════════════════════════════════════════ */}
          <div
            style={{
              position: "absolute",
              top: 1180,
              left: 0,
              right: 0,
              bottom: 0,
              padding: "0 90px",
            }}
          >
            {/* Riga 1 · Foto agente + nome + tagline */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 36,
                marginTop: 60,
              }}
            >
              {/* Foto agente cerchio sage o iniziali */}
              <div
                style={{
                  width: 180,
                  height: 180,
                  borderRadius: 999,
                  flexShrink: 0,
                  background: PALETTE.sage,
                  overflow: "hidden",
                  border: "5px solid #FFFFFF",
                  boxShadow:
                    "0 18px 40px rgba(28,28,26,0.18), 0 0 0 2px rgba(124,139,110,0.5)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "'Montserrat','Inter',sans-serif",
                  fontSize: 64,
                  fontWeight: 700,
                  color: "#FFFFFF",
                  letterSpacing: "0.02em",
                }}
              >
                {agentPhoto ? (
                  <img
                    src={agentPhoto}
                    alt=""
                    crossOrigin="anonymous"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  agentName
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase() || "P"
                )}
              </div>

              {/* Nome + tagline */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: "'Tinos','Cormorant Garamond',serif",
                    fontStyle: "italic",
                    fontWeight: 400,
                    fontSize: 78,
                    lineHeight: 0.96,
                    letterSpacing: "-0.018em",
                    color: PALETTE.ink,
                    marginBottom: 14,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  data-testid="story-agent-name"
                >
                  {agentName}
                </div>
                <div
                  style={{
                    fontFamily: "'Montserrat','Inter',sans-serif",
                    fontSize: 22,
                    fontWeight: 500,
                    color: PALETTE.inkSoft,
                    letterSpacing: "0.02em",
                    lineHeight: 1.3,
                  }}
                  data-testid="story-tagline"
                >
                  {tagline}
                </div>
              </div>
            </div>

            {/* Accent sage line */}
            <div
              style={{
                marginTop: 40,
                width: 72,
                height: 4,
                background: PALETTE.sage,
                borderRadius: 2,
              }}
            />

            {/* Bio breve */}
            {bio && (
              <div
                style={{
                  marginTop: 24,
                  fontFamily: "'Montserrat','Inter',sans-serif",
                  fontSize: 24,
                  fontWeight: 400,
                  color: PALETTE.ink,
                  lineHeight: 1.4,
                  letterSpacing: "0.005em",
                  maxWidth: 820,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {bio}
              </div>
            )}

            {/* CTA visita */}
            <div
              style={{
                position: "absolute",
                left: 90,
                right: 90,
                bottom: 90,
              }}
            >
              {/* Divider con label */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  marginBottom: 22,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "0.34em",
                    color: PALETTE.sage,
                    textTransform: "uppercase",
                  }}
                >
                  Visita la vetrina
                </div>
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    background: PALETTE.divider,
                  }}
                />
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "0.34em",
                    color: PALETTE.inkSoft,
                    textTransform: "uppercase",
                  }}
                >
                  {propertiesCount} immobili
                </div>
              </div>

              {/* URL grande */}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 18,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    fontFamily: "'Tinos','Cormorant Garamond',serif",
                    fontStyle: "italic",
                    fontWeight: 400,
                    fontSize: 48,
                    color: PALETTE.ink,
                    letterSpacing: "-0.01em",
                    lineHeight: 1,
                  }}
                  data-testid="story-url"
                >
                  {url}
                </div>
                <div
                  style={{
                    fontFamily: "'Montserrat','Inter',sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    letterSpacing: "0.28em",
                    color: PALETTE.caramel,
                    textTransform: "uppercase",
                  }}
                >
                  · Pronto.app
                </div>
              </div>

              {/* Footer marca */}
              <div
                style={{
                  marginTop: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: 14,
                  fontWeight: 500,
                  color: PALETTE.inkSoft,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                <span>· Tap del link in bio ·</span>
                <span>Made with Pronto</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
